/**
 * Parse un montant saisi par l'utilisateur (avec espaces, virgule décimale, points de milliers).
 * @param {string|number} value - Montant saisi (ex: "1 000 000", "1.000.000", "1,50")
 * @returns {number} Montant numérique, ou NaN si invalide
 */
export function parseAmount(value) {
    if (value == null) return NaN;
    if (typeof value === 'number') {
        if (Number.isFinite(value)) return value;
        return NaN;
    }
    let s = String(value).replace(/\s/g, '').trim();
    if (!s) return NaN;
    s = s.replace(',', '.');
    const parts = s.split('.');
    if (parts.length === 1) return parseFloat(parts[0]);
    const last = parts[parts.length - 1];
    if (last.length <= 2 && /^\d+$/.test(last)) {
        const intPart = parts.slice(0, -1).join('');
        return parseFloat(intPart + '.' + last);
    }
    return parseFloat(parts.join(''));
}

/**
 * Filtre la saisie en cours (évite les artefacts des inputs type="number" / step).
 * CDF : entiers uniquement. USD : jusqu'à 2 décimales.
 */
export function sanitizeAmountInput(raw, currency = 'CDF') {
    if (raw == null) return '';
    let s = String(raw).replace(/\s/g, '');
    const isCdf = (currency || 'CDF').toUpperCase() === 'CDF';

    if (isCdf) {
        return s.replace(/[^\d]/g, '');
    }

    s = s.replace(/[^\d.,]/g, '');
    const sepIndex = Math.max(s.lastIndexOf(','), s.lastIndexOf('.'));
    if (sepIndex < 0) {
        return s.replace(/[^\d]/g, '');
    }

    const intPart = s.slice(0, sepIndex).replace(/[^\d]/g, '');
    const decPart = s.slice(sepIndex + 1).replace(/[^\d]/g, '').slice(0, 2);
    const endsWithSep = /[.,]$/.test(s);

    if (decPart.length > 0) {
        return `${intPart}.${decPart}`;
    }
    if (endsWithSep && intPart.length > 0) {
        return `${intPart}.`;
    }
    return intPart;
}

/** Affichage normalisé après blur (100 reste 100, pas 99.99). */
export function formatAmountInputDisplay(value, currency = 'CDF') {
    const num = parseAmount(value);
    if (Number.isNaN(num) || !Number.isFinite(num)) return '';
    const isCdf = (currency || 'CDF').toUpperCase() === 'CDF';
    if (isCdf) {
        return String(Math.round(num));
    }
    const rounded = Math.round(num * 100) / 100;
    if (Number.isInteger(rounded)) {
        return String(rounded);
    }
    return rounded.toFixed(2).replace(/\.?0+$/, '') || rounded.toFixed(2);
}

/**
 * Montant pour l'API : CDF = entier, USD = 2 décimales max.
 */
export function normalizeAmountForApi(value, currency = 'CDF') {
    const num = parseAmount(value);
    if (Number.isNaN(num) || !Number.isFinite(num) || num < 0) return null;
    const isCdf = (currency || 'CDF').toUpperCase() === 'CDF';
    if (isCdf) {
        return Math.round(num);
    }
    return Math.round(num * 100) / 100;
}
